/**
 * 🔴 **WHAT A COMPANY'S LOGO IS DRAWN ON** (D139).
 *
 * Devin, 2026-09-19: *"when we bring back logos, we should always look at them and say whether they'll
 * look good with a white or a dark background if there is no background."*
 *
 * The backend LOOKS at every logo it stores (upload and research take alike) and writes one of four
 * words on the company's row; every screen that draws the mark reads the word and puts the mark on the
 * tile it asks for. The vocabulary and the two tile colours live HERE, in the one package the backend,
 * the shell and the kit all import, so the colour the analysis measured against and the colour a
 * screen paints are the same constant rather than two hexes that agree today.
 */

/**
 * - `own`    — the logo carries its own background (no meaningful transparency, or it paints its own
 *              frame). Drawn as it always was.
 * - `light`  — a mark on transparency that reads on WHITE. Drawn on a white tile, in either theme.
 * - `dark`   — a mark on transparency that reads on the Desk's DARK surface. Drawn on a dark tile.
 * - `either` — reads on both. Drawn as it always was.
 *
 * `null` on the row means "not looked at yet" — a logo stored before D139, filled the first time it is
 * served.
 */
export type LogoBackground = "own" | "light" | "dark" | "either";

export const LOGO_BACKGROUNDS: ReadonlyArray<LogoBackground> = ["own", "light", "dark", "either"];

export const isLogoBackground = (value: unknown): value is LogoBackground =>
  typeof value === "string" && (LOGO_BACKGROUNDS as readonly string[]).includes(value);

/** The white tile a `light` mark is drawn on — in the dark theme too, which is the whole point of it. */
export const LOGO_TILE_WHITE = "#FFFFFF";

/**
 * 🔴 **THE DESK'S DARK SURFACE** — the colour every card, drawer and sidebar in the dark theme is
 * painted with. `apps/shell/src/app/branding.ts` points its dark palette's `surface` at THIS constant,
 * and the logo analysis measures contrast against it, so "reads on dark" means "reads on the surface
 * this app actually paints" and cannot drift from it.
 */
export const DARK_SURFACE = "#1D2126";

/** The verdict in words, as a panel says it. */
export const LOGO_BACKGROUND_WORDS: Readonly<Record<LogoBackground, string>> = {
  light: "Reads on white",
  dark: "Reads on dark",
  either: "Reads on both",
  own: "Has its own background",
};

/** The tile colour a verdict asks for, or null for "as it always was" (`own`, `either`, not yet looked at). */
export function logoTile(background: LogoBackground | string | null | undefined): string | null {
  if (background === "light") return LOGO_TILE_WHITE;
  if (background === "dark") return DARK_SURFACE;
  return null;
}
